import { useState, useEffect, useRef } from "react";
import { Clock, Swords, Sparkles, Trophy, AlertCircle, Sword, Shield as ShieldIcon, Heart, Zap, Target, Award, PawPrint, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Empty, btn } from "@/components/Page";
import { RARITY } from "@/lib/game";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";

const STAT_ICONS = {
  strength: Sword,
  speed: Zap,
  defense: ShieldIcon,
  stamina: Heart,
  technique: Target,
  precision: Award,
};

const STAT_LABELS = {
  strength: "Strength",
  speed: "Speed",
  defense: "Defense",
  stamina: "Stamina",
  technique: "Technique",
  precision: "Precision",
};

const STAT_OPTIONS = [
  { value: "random", label: "Random" },
  ...Object.entries(STAT_LABELS).map(([value, label]) => ({ value, label })),
];

function rawSkillTotal(gladiator) {
  return Math.round(Object.values(gladiator?.skills || {}).reduce((total, value) => total + (value || 0), 0));
}

function statLabel(stat) {
  return STAT_LABELS[stat] || "Random";
}

function SalvageChips({ rew }) {
  if (!rew) return null;
  const rows = [];
  for (const kind of ["weapon", "armor"]) {
    const tally = rew[kind] || {};
    for (const [rarity, count] of Object.entries(tally)) {
      if (count) rows.push({ kind, rarity, count });
    }
  }
  if (!rows.length) return null;
  return (
    <div data-testid="salvage-rewards" className="flex flex-wrap gap-1.5">
      {rows.map((r, i) => {
        const meta = RARITY[r.rarity] || RARITY.common;
        return (
          <span key={i} className={`text-[11px] px-1.5 py-0.5 border rounded-sm ${meta.text} ${meta.border} ${meta.bg}`}>
            +{r.count} {r.rarity} {r.kind} frag{r.count > 1 ? "s" : ""}
          </span>
        );
      })}
    </div>
  );
}

function countdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "Complete";
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function QuestCard({ quest, onStart, disabled, gladName, statChoice = "random", onStatChoiceChange }) {
  const rew = quest.rewards;
  const statRange = `${(rew.base_stat_points * 0.8).toFixed(1)}-${(rew.base_stat_points * 1.2).toFixed(1)}`;
  const fragRange = rew.fragment_range ? `${rew.fragment_range[0]}-${rew.fragment_range[1]}` : "?";
  
  // Check if this is an equipment reward quest (e.g., blacksmith)
  const isEquipmentQuest = quest.reward_type === "equipment";
  
  return (
    <div className="border border-zinc-800 bg-zinc-950/40 p-4 hover:border-amber-700/50 transition-colors">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-cinzel text-amber-200 text-sm uppercase tracking-wide mb-1">{quest.name}</h3>
          <p className="text-xs text-zinc-500">{quest.description}</p>
        </div>
        <div className="text-right ml-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-600">{quest.tier_name}</div>
          <div className="flex items-center gap-1 text-xs text-zinc-400 mt-1">
            <Clock size={11} /> {quest.duration_hours}hr
          </div>
        </div>
      </div>
      
      {isEquipmentQuest ? (
        // Equipment reward display
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-purple-400 flex items-center gap-1 w-24">🛡️ Reward:</span>
            <span className="text-zinc-300">1 Common Equipment</span>
          </div>
          <div className="text-[10px] text-zinc-500 ml-[100px]">
            (Random weapon, offhand, armor, or helmet)
          </div>
        </div>
      ) : (
        // Normal rewards display
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="text-yellow-500 flex items-center gap-1 w-24"><GoldCoin size={11} /> Gold:</span>
            <span className="text-zinc-300">{rew.gold}</span>
          </div>
          {rew.follis && (
            <div className="flex items-center gap-2 text-xs">
              <span className="text-amber-700 flex items-center gap-1 w-24"><FollisCoin size={11} /> Follis:</span>
              <span className="text-zinc-300">{rew.follis}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs">
            <span className="text-blue-400 flex items-center gap-1 w-24"><Sparkles size={11} /> Fragments:</span>
            <span className="text-zinc-300">{fragRange}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-emerald-400 flex items-center gap-1 w-24"><Trophy size={11} /> Stat Points:</span>
            <span className="text-zinc-300">+{statRange} to {statChoice === "random" ? "a random stat" : statLabel(statChoice)}</span>
          </div>
          <div className="pt-1">
            <label className="block text-[10px] uppercase tracking-widest text-zinc-600 mb-1" htmlFor={`quest-stat-select-${quest.key}`}>
              Stat reward
            </label>
            <select
              id={`quest-stat-select-${quest.key}`}
              data-testid={`quest-stat-select-${quest.key}`}
              value={statChoice}
              onChange={(e) => onStatChoiceChange?.(quest.key, e.target.value)}
              disabled={disabled}
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 text-zinc-200 rounded-sm focus:border-amber-600 focus:outline-none text-xs disabled:opacity-60"
            >
              {STAT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
      
      <button
        onClick={() => onStart(quest, statChoice)}
        disabled={disabled}
        className={btn() + " w-full text-xs py-2"}
      >
        Send {gladName || "Gladiator"} on Quest
      </button>
    </div>
  );
}

function ActiveBeastHuntCard({ hunt, onComplete, onRecall, canClaim }) {
  const [, forceUpdate] = useState(0);
  const [showRecallModal, setShowRecallModal] = useState(false);
  
  useEffect(() => {
    const timer = setInterval(() => forceUpdate(n => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  
  const timeLeft = countdown(hunt.completes_at);
  const isComplete = timeLeft === "Complete";
  
  return (
    <>
      <div className="border border-teal-900/40 bg-teal-950/10 p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <PawPrint size={14} className="text-teal-400" />
              <h3 className="font-cinzel text-teal-200 text-sm">{hunt.gladiator_name}</h3>
            </div>
            <p className="text-xs text-zinc-400">Beast Hunt: {hunt.quest_name}</p>
          </div>
          {!isComplete && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-600">Time Remaining</div>
              <div className="text-teal-300 font-mono text-sm">{timeLeft}</div>
            </div>
          )}
        </div>
        
        {isComplete ? (
          <button
            onClick={() => onComplete(hunt.id)}
            disabled={canClaim === false}
            className={btn() + " w-full mt-3"}
          >
            Claim Beast (or Consolation)
          </button>
        ) : (
          <button
            onClick={() => setShowRecallModal(true)}
            className="w-full mt-3 px-4 py-2 text-xs rounded-sm border border-red-800/60 text-red-300 hover:bg-red-900/20 transition-colors"
          >
            Recall Early (Ticket Refunded)
          </button>
        )}
      </div>
      
      {showRecallModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowRecallModal(false)}>
          <div className="bg-zinc-900 border border-red-700 p-6 max-w-md w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-cinzel text-red-300 text-lg uppercase tracking-wide mb-4">
              Recall Beast Hunt?
            </h2>
            <p className="text-sm text-zinc-400 mb-4">
              Are you sure you want to recall <span className="text-teal-200">{hunt.gladiator_name}</span>? Your ticket will be refunded, but the hunt will be cancelled.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRecallModal(false)}
                className="flex-1 px-4 py-2 text-xs rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRecallModal(false);
                  onRecall(hunt.id);
                }}
                className="flex-1 px-4 py-2 text-xs rounded-sm bg-red-600 hover:bg-red-500 text-white transition-colors"
              >
                Recall Hunt
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ActiveQuestCard({ quest, onComplete, onRecall, canClaim }) {
  const [, forceUpdate] = useState(0);
  const [showRecallModal, setShowRecallModal] = useState(false);
  
  useEffect(() => {
    const timer = setInterval(() => forceUpdate(n => n + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  
  const timeLeft = countdown(quest.completes_at);
  const isComplete = timeLeft === "Complete";
  
  return (
    <>
      <div className="border border-amber-900/40 bg-amber-950/10 p-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Swords size={14} className="text-amber-400" />
              <h3 className="font-cinzel text-amber-200 text-sm">{quest.gladiator_name}</h3>
            </div>
            <p className="text-xs text-zinc-400">Quest: {quest.quest_name}</p>
            <p className="text-[10px] text-zinc-500 mt-1">Stat reward: {quest.selected_stat === "random" || !quest.selected_stat ? "Random" : statLabel(quest.selected_stat)}</p>
          </div>
          {!isComplete && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-600">Time Remaining</div>
              <div className="text-amber-300 font-mono text-sm">{timeLeft}</div>
            </div>
          )}
        </div>
        
        {isComplete ? (
          <button
            onClick={() => onComplete(quest.id)}
            disabled={canClaim === false}
            className={btn() + " w-full mt-3"}
          >
            Claim Rewards
          </button>
        ) : (
          <button
            onClick={() => setShowRecallModal(true)}
            className="w-full mt-3 px-4 py-2 text-xs rounded-sm border border-red-800/60 text-red-300 hover:bg-red-900/20 transition-colors"
          >
            Recall Early (Forfeit Rewards)
          </button>
        )}
      </div>
      
      {showRecallModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowRecallModal(false)}>
          <div className="bg-zinc-900 border border-red-700 p-6 max-w-md w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-cinzel text-red-300 text-lg uppercase tracking-wide mb-4 flex items-center gap-2">
              <AlertCircle size={18} /> Recall Gladiator?
            </h2>
            
            <p className="text-sm text-zinc-400 mb-6">
              Recall <span className="text-amber-200">{quest.gladiator_name}</span> from <span className="text-amber-200">{quest.quest_name}</span>?
              <br /><br />
              <span className="text-red-300 font-semibold">All rewards will be forfeited.</span>
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowRecallModal(false)}
                className="flex-1 px-4 py-2 rounded-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowRecallModal(false);
                  onRecall(quest.id);
                }}
                className="flex-1 px-4 py-2 rounded-sm border border-red-700 bg-red-900/20 text-red-300 hover:bg-red-900/40 transition-colors"
              >
                Recall & Forfeit
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function WeeklyProgressBar({ stats }) {
  if (!stats) return null;
  
  const raw = stats.raw_sp_earned || 0;
  const actual = stats.actual_sp_applied || 0;
  const fullPct = Math.min(100, (raw / 20) * 100);
  const reducedPct = raw > 20 ? Math.min(100, ((raw - 20) / 20) * 100) : 0;
  
  return (
    <div className="border border-zinc-800 bg-zinc-950/40 p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-cinzel text-amber-300 text-sm uppercase tracking-wide">Weekly Quest Stats</h3>
        <div className="text-xs text-zinc-500">
          Resets: {stats.week_reset_at ? countdown(stats.week_reset_at) : "N/A"}
        </div>
      </div>
      
      <div className="text-xs text-zinc-400 mb-3">
        <span className="text-amber-300">{raw.toFixed(1)}</span> raw SP earned → <span className="text-emerald-300">{actual.toFixed(1)}</span> actual stats gained
      </div>
      
      <div className="space-y-2">
        <div>
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-emerald-400">Full Value (0-20 SP)</span>
            <span className="text-zinc-500">{Math.min(raw, 20).toFixed(1)}/20</span>
          </div>
          <div className="h-2 bg-zinc-900 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${fullPct}%` }} />
          </div>
        </div>
        
        <div>
          <div className="flex items-center justify-between text-[10px] mb-1">
            <span className="text-yellow-400">Reduced (21-40 SP) - 35% efficiency</span>
            <span className="text-zinc-500">{Math.max(0, Math.min(raw - 20, 20)).toFixed(1)}/20</span>
          </div>
          <div className="h-2 bg-zinc-900 rounded-full overflow-hidden">
            <div className="h-full bg-yellow-500 transition-all" style={{ width: `${reducedPct}%` }} />
          </div>
        </div>
        
        {raw >= 40 && (
          <div className="flex items-center gap-2 text-[10px] text-red-400">
            <AlertCircle size={12} />
            <span>Weekly cap reached (41+ SP) - No more stat rewards this week</span>
          </div>
        )}
      </div>
    </div>
  );
}

function QuestRewardModal({ rewards, gladName, onClose }) {
  const StatIcon = STAT_ICONS[rewards.stat_points.stat] || Trophy;
  const statName = statLabel(rewards.stat_points.stat);
  
  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-zinc-900 border border-amber-700 p-6 max-w-md w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-cinzel text-amber-300 text-lg uppercase tracking-wide mb-4 flex items-center gap-2">
          <Trophy size={18} /> Quest Complete!
        </h2>
        
        <p className="text-sm text-zinc-400 mb-4">
          <span className="text-amber-200">{gladName}</span> has returned!
        </p>
        
        <div className="space-y-3 mb-6">
          <div className="flex items-center gap-3">
            <GoldCoin size={24} className="text-yellow-500" />
            <div>
              <div className="text-xs text-zinc-500">Gold Earned</div>
              <div className="text-amber-200 font-semibold">{rewards.gold} gold</div>
            </div>
          </div>
          
          {rewards.follis && rewards.follis > 0 && (
            <div className="flex items-center gap-3">
              <FollisCoin size={24} className="text-amber-700" />
              <div>
                <div className="text-xs text-zinc-500">Follis Earned</div>
                <div className="text-amber-600 font-semibold">{rewards.follis} Follis</div>
              </div>
            </div>
          )}
          
          <div>
            <div className="text-xs text-zinc-500 mb-2 flex items-center gap-1">
              <Sparkles size={12} /> Fragments Earned
            </div>
            <SalvageChips rew={rewards.fragments} />
          </div>
          
          <div className="flex items-center gap-3 p-3 bg-emerald-950/20 border border-emerald-900/40 rounded-sm">
            <StatIcon size={24} className="text-emerald-400" />
            <div>
              <div className="text-xs text-zinc-500">Stat Gained</div>
              <div className="text-emerald-300 font-semibold">
                +{rewards.stat_points.actual.toFixed(2)} {statName}
              </div>
              {rewards.stat_points.rolled !== rewards.stat_points.actual && (
                <div className="text-[10px] text-zinc-600">
                  ({rewards.stat_points.rolled.toFixed(2)} → {rewards.stat_points.actual.toFixed(2)} after weekly efficiency)
                </div>
              )}
            </div>
          </div>
        </div>
        
        <button onClick={onClose} className={btn() + " w-full"}>
          Continue
        </button>
      </div>
    </div>
  );
}

export default function TimedQuests() {
  const { user, refreshUser } = useAuth();
  const [glads, setGlads] = useState(null);
  const [selectedGlad, setSelectedGlad] = useState(null);
  const [quests, setQuests] = useState(null);
  const [activeQuests, setActiveQuests] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [questUsage, setQuestUsage] = useState(null);  // NEW: Track weekly quest usage
  const [busy, setBusy] = useState(false);
  const [rewardModal, setRewardModal] = useState(null);
  const [questStatChoices, setQuestStatChoices] = useState({});
  
  // Beast hunt state
  const [beastHuntData, setBeastHuntData] = useState(null);
  const [activeBeastHunt, setActiveBeastHunt] = useState(null);
  const [beastRewardModal, setBeastRewardModal] = useState(null);
  
  const loadGlads = () => {
    api.get("/gladiators").then((r) => {
      // Include all active gladiators (even crown holders) for beast hunts
      const active = r.data.active.filter(g => !g.retired);
      setGlads(active);
      if (active.length > 0 && !selectedGlad) {
        setSelectedGlad(active[0].id);
      }
    }).catch(() => setGlads([]));
  };
  
  const loadQuests = (gladId) => {
    if (!gladId) return;
    api.get(`/timed-quests/available?gladiator_id=${gladId}`)
      .then((r) => setQuests(r.data))
      .catch(() => setQuests(null));
  };
  
  const loadActive = () => {
    api.get("/timed-quests/active")
      .then((r) => setActiveQuests(r.data))
      .catch(() => setActiveQuests(null));
  };
  
  const loadWeeklyStats = (gladId) => {
    if (!gladId) return;
    api.get(`/timed-quests/weekly-stats/${gladId}`)
      .then((r) => setWeeklyStats(r.data))
      .catch(() => setWeeklyStats(null));
  };
  
  const loadQuestUsage = (gladId) => {
    if (!gladId) return;
    api.get(`/timed-quests/gladiator-usage/${gladId}`)
      .then((r) => setQuestUsage(r.data))
      .catch(() => setQuestUsage(null));
  };
  
  const loadBeastHunts = (gladiatorId = selectedGlad) => {
    const query = gladiatorId ? `?gladiator_id=${gladiatorId}` : "";
    api.get(`/timed-quests/beast-hunts/available${query}`)
      .then((r) => {
        setBeastHuntData(r.data);
        // Always check if there's an active hunt (even if vivarium not available)
        api.get("/timed-quests/active")
          .then((res) => {
            const hunt = res.data.active_quests?.find(q => q.quest_type === "beast_hunt");
            setActiveBeastHunt(hunt || null);
          })
          .catch(() => setActiveBeastHunt(null));
      })
      .catch(() => {
        setBeastHuntData(null);
        setActiveBeastHunt(null);
      });
  };
  
  useEffect(() => {
    loadGlads();
    loadActive();
    loadBeastHunts();
  }, []);
  
  useEffect(() => {
    if (selectedGlad) {
      loadQuests(selectedGlad);
      loadWeeklyStats(selectedGlad);
      loadQuestUsage(selectedGlad);
      loadBeastHunts(selectedGlad);
    }
  }, [selectedGlad]);
  
  const startQuest = async (quest, selectedStat = "random") => {
    if (!selectedGlad) {
      toast.error("Please select a gladiator");
      return;
    }
    
    setBusy(true);
    try {
      await api.post("/timed-quests/start", {
        gladiator_id: selectedGlad,
        quest_key: quest.key,
        selected_stat: selectedStat,
      });
      toast.success("Quest started!");
      loadActive();
      loadGlads();
      if (selectedGlad) loadQuestUsage(selectedGlad);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const completeQuest = async (questId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/timed-quests/complete?quest_id=${questId}`);
      setRewardModal(data.rewards);
      loadActive();
      loadGlads();
      if (selectedGlad) loadWeeklyStats(selectedGlad);
      await refreshUser();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const recallQuest = async (questId) => {
    setBusy(true);
    try {
      await api.post(`/timed-quests/recall?quest_id=${questId}`);
      toast.success("Gladiator recalled");
      loadActive();
      loadGlads();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const purchaseTicket = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/timed-quests/beast-hunt/purchase-ticket");
      toast.success(data.message);
      loadBeastHunts();
      await refreshUser();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const startBeastHunt = async (tier) => {
    if (!selectedGlad) {
      toast.error("Please select a gladiator");
      return;
    }
    
    setBusy(true);
    try {
      const { data } = await api.post("/timed-quests/beast-hunt/start", {
        gladiator_id: selectedGlad,
        tier: tier,
      });
      toast.success(data.message);
      loadBeastHunts();
      loadActive();  // Refresh active quests display
      loadGlads();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const completeBeastHunt = async (questId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/timed-quests/beast-hunt/complete?quest_id=${questId}`);
      setBeastRewardModal(data);
      loadBeastHunts();
      loadActive();  // Refresh active quests display
      loadGlads();
      await refreshUser();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  const recallBeastHunt = async (questId) => {
    setBusy(true);
    try {
      await api.post(`/timed-quests/beast-hunt/recall?quest_id=${questId}`);
      toast.success("Gladiator recalled, ticket refunded");
      loadBeastHunts();
      loadActive();  // Refresh active quests display
      loadGlads();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };
  
  if (glads === null) return <Loader label="Loading quests..." />;
  
  // Crown holders can hunt beasts but not do regular quests
  const eligibleGlads = glads.filter(g => !g.golden_crown && !g.retired);
  const selectedGladObj = glads.find(g => g.id === selectedGlad); // Use full list for selection
  const isVip = user?.vip;
  const maxSlots = isVip ? 2 : 1;
  const slotsUsed = activeQuests?.slots_used || 0;

  const handleQuestStatChoiceChange = (questKey, value) => {
    setQuestStatChoices((prev) => ({ ...prev, [questKey]: value }));
  };
  
  // Check if selected gladiator is already on a regular quest (exclude beast hunts)
  const gladOnQuest = activeQuests?.active_quests?.find(q => q.gladiator_id === selectedGlad && q.quest_type !== "beast_hunt");
  
  // Check if selected gladiator is crown holder (can't do regular quests)
  const isCrownHolder = selectedGladObj?.golden_crown;
  
  return (
    <div>
      {/* Timed Quests Banner */}
      <div className="relative mb-6 overflow-hidden border border-cyan-900/40 rounded-sm">
        <img 
          src="/quests-banner.png" 
          alt="Timed Quests - Command Post" 
          className="w-full h-44 sm:h-60 object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <h2 className="absolute bottom-3 left-5 font-cinzel text-2xl sm:text-3xl text-cyan-200 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] flex items-center gap-2">
          <Clock size={26} /> Timed Quests
        </h2>
      </div>

      <div className="mb-6">
        <p className="text-sm text-zinc-400 mb-4">
          Send your gladiators on timed missions for gold, fragments, and permanent stat boosts. 
          Gladiators on quests cannot challenge or be challenged, but can still train. 
          Crown holders must remain in the arena and cannot quest.
        </p>
        
        {glads.length === 0 ? (
          <Empty label="No gladiators available. Recruit gladiators to start questing." />
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2" htmlFor="timed-quest-gladiator-select">
                Select Gladiator
              </label>
              <select
                id="timed-quest-gladiator-select"
                data-testid="timed-quest-gladiator-select"
                value={selectedGlad || ""}
                onChange={(event) => setSelectedGlad(event.target.value)}
                className="w-full max-w-md px-3 py-2.5 bg-zinc-950/60 border border-zinc-800 text-zinc-200 rounded-sm focus:border-amber-600 focus:outline-none text-sm"
              >
                {glads.map((gladiator) => (
                  <option key={gladiator.id} value={gladiator.id}>
                    {gladiator.name} · {gladiator.cls}{gladiator.golden_crown ? " · Crown Holder" : ""}
                  </option>
                ))}
              </select>

              {selectedGladObj && (() => {
                const questStats = selectedGladObj.quest_stats || {};
                const lifetimeCompleted = questStats.total_completed || 0;
                const lifetimeGains = questStats.total_stat_gains || 0;
                const onQuestNow = activeQuests?.active_quests?.some(
                  (quest) => quest.gladiator_id === selectedGladObj.id && quest.quest_type !== "beast_hunt"
                );
                const questEligibility = quests?.eligibility?.gladiator_id === selectedGladObj.id ? quests.eligibility : null;
                const huntEligibility = beastHuntData?.eligibility?.gladiator_id === selectedGladObj.id ? beastHuntData.eligibility : null;
                const questTierNames = questEligibility?.quest_tiers?.map((tier) => tier.name).join(" · ") || "Loading";
                const huntTierNames = huntEligibility
                  ? (huntEligibility.hunt_tiers?.map((tier) => tier.name).join(" · ") || "None")
                  : "Loading";

                return (
                  <div data-testid="selected-gladiator-quest-progress" className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 border-l-2 border-amber-700/70 bg-zinc-950/30 px-3 py-2.5">
                    <span data-testid="selected-gladiator-name" className="font-cinzel text-xs text-amber-200">
                      {selectedGladObj.name}
                    </span>
                    <span className="text-[11px] text-zinc-500">
                      Lifetime quests:{" "}
                      <span data-testid="selected-gladiator-quest-completed" className="text-zinc-200">
                        {lifetimeCompleted}
                      </span>
                    </span>
                    <span className="text-[11px] text-zinc-500">
                      Permanent stat gains:{" "}
                      <span data-testid="selected-gladiator-quest-stat-gains" className="text-emerald-400">
                        +{lifetimeGains % 1 === 0 ? lifetimeGains : lifetimeGains.toFixed(2)}
                      </span>
                    </span>
                    <span data-testid="selected-gladiator-quest-eligibility" className="text-[11px] text-zinc-400">
                      Quest tiers: {questTierNames}
                    </span>
                    <span data-testid="selected-gladiator-hunt-rating" className="text-[11px] text-teal-300">
                      Hunt rating: {huntEligibility?.hunt_rating ?? "—"}
                    </span>
                    <span data-testid="selected-gladiator-hunt-eligibility" className="text-[11px] text-zinc-400">
                      Hunt tiers: {huntTierNames}
                    </span>
                    {onQuestNow && (
                      <span data-testid="selected-gladiator-on-quest" className="text-[11px] text-amber-400">On quest</span>
                    )}
                  </div>
                );
              })()}
              {isCrownHolder && (
                <p data-testid="crown-holder-quest-notice" className="mt-2 text-xs text-amber-400 flex items-center gap-1.5">
                  <Crown size={12} /> Crown holders cannot do regular quests, but can hunt beasts below.
                </p>
              )}
            </div>
            
            {/* Quest Usage Display */}
            {selectedGlad && questUsage && (
              <div className="mb-4 p-3 bg-zinc-900/60 border border-zinc-800 rounded">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-zinc-400">Weekly Quest Limit:</span>
                  <span className={`font-semibold ${questUsage.quests_remaining > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {questUsage.quests_used} / {questUsage.weekly_limit} used
                  </span>
                </div>
                {questUsage.quests_remaining === 0 && (
                  <div className="mt-2 text-[10px] text-red-400">
                    ⚠️ Quest limit reached. Resets Sunday midnight.
                  </div>
                )}
                {questUsage.quests_remaining > 0 && (
                  <div className="mt-1 text-[10px] text-zinc-500">
                    {questUsage.quests_remaining} quest{questUsage.quests_remaining !== 1 ? 's' : ''} remaining this week
                  </div>
                )}
              </div>
            )}
            
            {selectedGlad && weeklyStats && (
              <WeeklyProgressBar stats={weeklyStats} />
            )}
          </>
        )}
      </div>
      
      {/* Active Quests */}
      {activeQuests && activeQuests.active_quests.length > 0 && (
        <div className="mb-6">
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center justify-between">
            <span>Active Quests ({slotsUsed}/{maxSlots})</span>
            {!isVip && <span className="text-[10px] text-zinc-600 normal-case">VIP: +1 quest slot</span>}
          </h2>
          <div className="space-y-3">
            {activeQuests.active_quests.map(quest => (
              quest.quest_type === "beast_hunt" ? (
                <ActiveBeastHuntCard
                  key={quest.id}
                  hunt={quest}
                  onComplete={completeBeastHunt}
                  onRecall={recallBeastHunt}
                  canClaim={!busy}
                />
              ) : (
                <ActiveQuestCard
                  key={quest.id}
                  quest={quest}
                  onComplete={completeQuest}
                  onRecall={recallQuest}
                  canClaim={!busy}
                />
              )
            ))}
          </div>
        </div>
      )}
      
      {/* Available Quests */}
      {selectedGlad && quests && (
        <div>
          <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3">
            Available Quests
            {quests.refresh_at && (
              <span className="text-[10px] text-zinc-600 ml-2 normal-case">
                New quests in {countdown(quests.refresh_at)}
              </span>
            )}
          </h2>
          
          {isCrownHolder ? (
            <div className="border border-amber-900/40 bg-amber-950/10 p-4 text-center">
              <p className="text-sm text-amber-300 flex items-center justify-center gap-2">
                <Crown size={16} /> Crown holders cannot do regular quests — scroll down for beast hunts!
              </p>
            </div>
          ) : gladOnQuest ? (
            <div className="border border-amber-900/40 bg-amber-950/10 p-4 text-center">
              <p className="text-sm text-amber-300">
                {selectedGladObj?.name} is currently on a quest. Complete or recall it first.
              </p>
            </div>
          ) : slotsUsed >= maxSlots ? (
            <div className="border border-red-900/40 bg-red-950/10 p-4 text-center">
              <p className="text-sm text-red-300">
                All quest slots full ({slotsUsed}/{maxSlots}). Complete or recall an active quest first.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(quests.quests_by_tier || {}).map(([tierKey, tierQuests]) => (
                <div key={tierKey}>
                  <h3 className="font-cinzel text-zinc-400 uppercase tracking-wide text-xs mb-3">
                    {tierQuests[0]?.tier_name || tierKey}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {tierQuests.map(quest => (
                      <QuestCard
                        key={quest.key}
                        quest={quest}
                        onStart={startQuest}
                        disabled={busy || gladOnQuest || slotsUsed >= maxSlots || isCrownHolder}
                        gladName={selectedGladObj?.name}
                        statChoice={questStatChoices[quest.key] || "random"}
                        onStatChoiceChange={handleQuestStatChoiceChange}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Beast Hunts Section - Moved to bottom */}
      {beastHuntData && beastHuntData.available && (
        <div className="mb-6 mt-8">
          <h2 className="font-cinzel text-teal-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2">
            <PawPrint size={16} /> Beast Capture Quests
          </h2>
          
          <div className="border border-teal-900/40 bg-teal-950/10 p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm text-teal-200 font-semibold">Venatore Tickets: {beastHuntData.venatore_tickets}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  Earn 1 ticket per 100 PvP battles or buy for 100 Gold
                </p>
              </div>
              <button
                onClick={purchaseTicket}
                disabled={busy || (user?.gold || 0) < 100}
                className="px-3 py-2 text-xs border border-amber-700 text-amber-300 hover:bg-amber-900/20 rounded-sm transition-colors disabled:opacity-40"
              >
                Buy Ticket (100 Gold)
              </button>
            </div>
            
            {beastHuntData.at_capacity && (
              <div className="mt-3 p-2 border border-rose-700/40 bg-rose-950/10 rounded-sm">
                <p className="text-xs text-rose-300">
                  Vivarium at capacity ({beastHuntData.beast_count}/{beastHuntData.capacity}). Release or sell a beast first.
                </p>
              </div>
            )}
          </div>
          
          {activeBeastHunt ? (
            <div className="border border-teal-900/40 bg-teal-950/10 p-4 mb-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Swords size={14} className="text-teal-400" />
                    <h3 className="font-cinzel text-teal-200 text-sm">{activeBeastHunt.gladiator_name}</h3>
                  </div>
                  <p className="text-xs text-zinc-400">{activeBeastHunt.quest_name}</p>
                  <div className="mt-2 flex items-center gap-2">
                    <Clock size={12} className="text-zinc-500" />
                    <span className="text-xs text-zinc-400">
                      {countdown(activeBeastHunt.completes_at) === "Complete" 
                        ? <span className="text-emerald-400 font-semibold">Ready to claim!</span>
                        : <span>Completes in <span className="text-teal-300 font-mono">{countdown(activeBeastHunt.completes_at)}</span></span>
                      }
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-3 flex gap-2">
                {countdown(activeBeastHunt.completes_at) === "Complete" ? (
                  <button
                    onClick={() => completeBeastHunt(activeBeastHunt.id)}
                    disabled={busy}
                    className={btn() + " flex-1"}
                  >
                    Claim Beast (or Consolation)
                  </button>
                ) : (
                  <button
                    onClick={() => recallBeastHunt(activeBeastHunt.id)}
                    disabled={busy}
                    className="flex-1 px-4 py-2 text-xs rounded-sm border border-red-800/60 text-red-300 hover:bg-red-900/20 transition-colors disabled:opacity-40"
                  >
                    Recall Early (Ticket Refunded)
                  </button>
                )}
              </div>
            </div>
          ) : null}
          
          {activeBeastHunt && (
            <div className="border border-amber-900/60 bg-amber-950/20 p-4 mb-4 flex items-start gap-3">
              <AlertCircle size={18} className="text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-amber-300 font-semibold">
                  Hunt in Progress
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  <b className="text-amber-200">{activeBeastHunt.gladiator_name}</b> is currently on a beast hunt. Only one gladiator can hunt at a time. Complete or recall the active hunt before starting a new one.
                </p>
              </div>
            </div>
          )}
          
          <div className="space-y-3">
              {Object.entries(beastHuntData.tiers || {}).map(([tierKey, tierData]) => {
                const eligibleHuntTierKeys = new Set(beastHuntData.eligibility?.hunt_tiers?.map((tier) => tier.key) || []);
                const meetsSkillReq = eligibleHuntTierKeys.has(tierKey);
                if (selectedGlad && !meetsSkillReq) {
                  return null;
                }
                
                const canStart = selectedGlad && 
                  beastHuntData.venatore_tickets >= tierData.ticket_cost &&
                  !beastHuntData.at_capacity &&
                  !gladOnQuest &&
                  !activeBeastHunt &&
                  meetsSkillReq;
                
                return (
                  <div key={tierKey} className={`border rounded-sm p-4 transition-opacity ${activeBeastHunt ? 'border-zinc-800 bg-zinc-950/20 opacity-50' : 'border-zinc-800 bg-zinc-950/40'}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-cinzel text-teal-200 text-sm uppercase tracking-wide mb-1">
                          {tierData.name}
                        </h3>
                        <p className="text-xs text-zinc-500">
                          {tierData.duration_hours}hr hunt · {Math.round(tierData.success_rate * 100)}% success rate · Requires {tierData.display_req || `${tierData.skill_min}+ skill`}
                        </p>
                      </div>
                      <div className="text-right ml-3">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600">Cost</div>
                        <div className="text-teal-300">{tierData.ticket_cost} ticket</div>
                      </div>
                    </div>
                    
                    <div className="space-y-1.5 mb-3 text-xs">
                      <div className="text-zinc-400">
                        <span className="text-zinc-500">On Success:</span> Capture a beast!
                      </div>
                      <div className="text-zinc-400">
                        <span className="text-zinc-500">On Failure:</span> {tierData.consolation.amount_range[0]}-{tierData.consolation.amount_range[1]} {tierData.consolation.fragment_type} fragments
                      </div>
                    </div>
                    
                    <button
                      onClick={() => startBeastHunt(tierKey)}
                      disabled={busy || !canStart || !!activeBeastHunt}
                      className={btn() + " w-full disabled:opacity-40 disabled:cursor-not-allowed"}
                    >
                      {activeBeastHunt ? "Another Glad is Hunting" :
                       !selectedGlad ? "Select a Gladiator" : 
                       beastHuntData.venatore_tickets < tierData.ticket_cost ? "Need Ticket" :
                       beastHuntData.at_capacity ? "Vivarium Full" :
                       gladOnQuest ? "Gladiator Busy" : !meetsSkillReq ? `Need ${tierData.display_req || `${tierData.skill_min}+ skill`}` : "Start Hunt"}
                    </button>
                  </div>
                );
              })}
            </div>
        </div>
      )}
      
      {rewardModal && (
        <QuestRewardModal
          rewards={rewardModal}
          gladName={activeQuests?.active_quests?.find(q => q.id === rewardModal.quest_id)?.gladiator_name || "Your gladiator"}
          onClose={() => {
            setRewardModal(null);
            if (selectedGlad) loadWeeklyStats(selectedGlad);
          }}
        />
      )}
      
      {/* Beast Hunt Reward Modal */}
      {beastRewardModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setBeastRewardModal(null)}>
          <div className="bg-zinc-900 border border-teal-700 p-6 max-w-md w-full rounded-sm" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-cinzel text-teal-300 text-lg uppercase tracking-wide mb-4 flex items-center gap-2">
              <PawPrint size={20} /> {beastRewardModal.success ? "Beast Captured!" : "Hunt Failed"}
            </h2>
            
            {beastRewardModal.success ? (
              <div className="space-y-4">
                <p className="text-sm text-zinc-300">
                  <b className="text-teal-200">{beastRewardModal.gladiator_name}</b> has captured a <b className="text-amber-300">{beastRewardModal.beast_display_name}</b>!
                </p>
                <div className="border border-teal-800 bg-teal-950/20 p-4 rounded-sm">
                  <div className="text-sm text-zinc-200 space-y-1">
                    <div>Tier: <span className="text-teal-300">{beastRewardModal.beast.tier}</span></div>
                    <div>Primary: <span className="text-emerald-300">+{beastRewardModal.beast.primary_value} {beastRewardModal.beast.primary_stat}</span></div>
                    <div>Affix: <span className="text-sky-300">+{beastRewardModal.beast.affix_value}% {beastRewardModal.beast.affix_stat}</span></div>
                  </div>
                </div>
                <p className="text-xs text-zinc-500 italic">
                  Visit the Vivarium to equip this beast to a gladiator!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-zinc-300">
                  The beast escaped! <b className="text-teal-200">{beastRewardModal.gladiator_name}</b> returns with consolation fragments.
                </p>
                <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
                  <div className="text-sm text-zinc-200">
                    +{beastRewardModal.consolation.amount} {beastRewardModal.consolation.fragment_type} fragments
                  </div>
                </div>
              </div>
            )}
            
            <button
              onClick={() => setBeastRewardModal(null)}
              className={btn() + " w-full mt-6"}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
